import { BrowserRouter } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { fiFI as coreFiFI } from '@mui/material/locale';
import { fiFI } from '@mui/x-date-pickers/locales';
import '../i18n';
import { palette } from 'theme/palette';
import AppRoutes from './AppRoutes';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import fiLocale from 'date-fns/locale/fi';
import { useMst } from '../model/Root';
import { useEffect, useRef } from 'react';
import { listOcrDocuments, listAudiobooksLight } from 'services/csAudiobookApi';
import { Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react-lite';

const App = observer(() => {
  const {
    userStore: { getMe, token, state },
    ocrStore,
  } = useMst();

  useEffect(() => {
    if (token) {
      getMe();
    }
  }, [getMe, token]);

  // Pre-warm CoreSkills OCR service after initial render is idle (do not slow first loads)
  const prewarmedRef = useRef(false);
  useEffect(() => {
    if (prewarmedRef.current) return;
    prewarmedRef.current = true;
    const prewarm = () => {
      // Fire-and-forget; explicitly swallow any network errors to avoid unhandled rejections
      listOcrDocuments({ document_type: 'audiobook', limit: 1 }).catch(() => null);
      listAudiobooksLight({ limit: 1 }).catch(() => null);
    };
    type RequestIdle = (cb: () => void, opts?: { timeout?: number }) => void;
    const ric = (window as unknown as { requestIdleCallback?: RequestIdle }).requestIdleCallback;
    if (typeof ric === 'function') {
      ric(prewarm, { timeout: 4000 });
    } else {
      setTimeout(prewarm, 3000);
    }
  }, []);

  const theme = createTheme(
    {
      palette: {
        primary: { main: palette.green },
        secondary: { main: palette.amber },
      },
      typography: {
        fontFamily: 'Poppins',
      },
    },
    fiFI, // x-date-pickers translations
    coreFiFI // core translations
  );

  if (state === 'IN_PROGRESS') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fiLocale}>
          <AppRoutes />
        </LocalizationProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
});

export default App;
